
import React, { useEffect, useState, useCallback } from 'react';
import { Table, Modal } from 'antd';
import { User } from '@models/user';
import { Billing } from '@models/billing';
import { ArtistRep } from '../../../data/models/artist-rep';
import { App, Button, Col, DatePicker, DatePickerProps, Empty, Input, notification, Progress, Row, Switch, Typography, Select, Tooltip, Divider } from 'antd';
import { SaveOutlined, SearchOutlined, DeleteOutlined, ExclamationCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { makeRequest } from '@helpers/axios-manipulator';
import { getCurrentUtcDate } from '../../helpers/current-utc-date-generator';
import Title from 'antd/es/typography/Title';
import RepresentationContract from '../../components/Billing/RepresentationContract';
import { green, red, orange } from '@ant-design/colors';
import NewUserModal from '../../components/NewUserModal';
import AddBillingUserModal from '../../components/AddBillingUserModal';
import dayjs from 'dayjs';

// ...existing code...


const ContractsScreen = () => {
        // Business name search state
        const [businessNameSearch, setBusinessNameSearch] = useState('');
        // Add New contract states
        const [isAddingNew, setIsAddingNew] = useState(false);
        const [addNewBusinessId, setAddNewBusinessId] = useState<string | undefined>(undefined);
        const [addNewBusinessOptions, setAddNewBusinessOptions] = useState<any[]>([]);
        const [existingBusinessIds, setExistingBusinessIds] = useState<string[]>([]);
        const [searchOptions, setSearchOptions] = useState<any[]>([]);
        const [searchValue, setSearchValue] = useState('');
    const navigate = useNavigate();
    const [searchDropdownVisible, setSearchDropdownVisible] = useState(false);
    const [searchInputValue, setSearchInputValue] = useState('');
    // State for all billing contracts table
    const [allBillings, setAllBillings] = useState<any[]>([]);
    const [allBillingsLoading, setAllBillingsLoading] = useState(false);

    // Helper to fetch and sort all billings
    const fetchAndSetAllBillings = useCallback(async () => {
        const allBillingsResponse = await makeRequest({
            origin: window.location.origin,
            method: 'get',
            url: '/api-billing-search?query=',
            headers: {},
        });
        const sorted = (allBillingsResponse || []).sort((a: any, b: any) => String(b.contract_id).localeCompare(String(a.contract_id)));
        setAllBillings(sorted);
    }, []);

    const { Text } = Typography;
    const [searchLoading, setSearchLoading] = useState(false);
    const [data, setData] = useState<User>({} as User);
    const [contracts, setContracts] = useState<ArtistRep[]>([]);
    const [billing, setBilling] = useState<Billing>({} as Billing);
    const [contractsKey, setContractsKey] = useState<number>(1);
    const [highlightedRowKey, setHighlightedRowKey] = useState<string | undefined>(undefined);
    const [hasNewContract, setHasNewContract] = useState(false);
    const [loading, setLoading] = useState(false);
    const [contractsLoading, setContractsLoading] = useState(false);
    const [addNewErrors, setAddNewErrors] = useState<{ [key: string]: string }>({});
    const { message } = App.useApp();
    const [api, contextHolder] = notification.useNotification();
    const [lastUpdatedByUsername, setLastUpdatedByUsername] = useState<string>('');

    // Billing users state
    const [billingUsers, setBillingUsers] = useState<any[]>([]);
    const [billingUsersLoading, setBillingUsersLoading] = useState(false);
    const [addBillingUserModalOpen, setAddBillingUserModalOpen] = useState(false);

    // Fetch billing users when billing is selected
    const fetchBillingUsers = useCallback(async (billingId: number) => {
        if (billingId === undefined || billingId === null) {
            setBillingUsers([]);
            return;
        }

        setBillingUsersLoading(true);
        try {
            const response = await makeRequest({
                origin: window.location.origin,
                method: 'get',
                url: `/api/billing/${billingId}/users`,
                headers: {},
            });
            setBillingUsers(response || []);
        } catch (error: any) {
            console.error('Error fetching billing users:', error);
            if (error?.response?.status !== 403) {
                message.error('Failed to load authorized users.');
            }
            setBillingUsers([]);
        } finally {
            setBillingUsersLoading(false);
        }
    }, []);

    // Toggle user status
    const handleToggleUserStatus = async (userId: number, currentStatus: boolean) => {
        try {
            await makeRequest({
                origin: window.location.origin,
                method: 'put',
                url: `/api/billing-users/${userId}/status`,
                headers: { 'Content-Type': 'application/json' },
                data: { is_active: !currentStatus },
            });

            message.success(
                !currentStatus
                    ? 'User access enabled'
                    : 'User access disabled. User will be logged out.'
            );

            // Refresh the billing users list
            if (billing?.id !== undefined && billing?.id !== null) {
                await fetchBillingUsers(billing.id);
            }
        } catch (error) {
            console.error('Error toggling user status:', error);
            message.error('Failed to update user status.');
        }
    };

    // Remove user from billing contract
    const handleRemoveUser = (userId: number, userEmail: string) => {
        Modal.confirm({
            title: 'Remove User Access',
            icon: <ExclamationCircleOutlined />,
            content: `Are you sure you want to remove access for ${userEmail}? They will no longer be able to log in to this business account.`,
            okText: 'Remove',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                try {
                    await makeRequest({
                        origin: window.location.origin,
                        method: 'delete',
                        url: `/api/billing-users/${userId}`,
                        headers: {},
                    });

                    message.success('User access removed successfully.');

                    // Refresh the billing users list
                    if (billing?.id !== undefined && billing?.id !== null) {
                        await fetchBillingUsers(billing.id);
                    }
                } catch (error) {
                    console.error('Error removing user:', error);
                    message.error('Failed to remove user access.');
                }
            },
        });
    };

    // Table columns for all contracts
    const billingTableColumns = [
        {
            title: 'Contract ID',
            dataIndex: 'contract_id',
            key: 'contract_id',
            render: (text: string, record: any) =>
                text !== null && text !== undefined ? (
                    <a
                        style={{ cursor: 'pointer' }}
                        onClick={async (e) => {
                            e.preventDefault();
                            try {
                                const billingResponse = await makeRequest({
                                    origin: window.location.origin,
                                    method: 'get',
                                    url: `/api-billing/${record.business_id}`,
                                    headers: {},
                                });
                                setBilling(billingResponse);
                                setData(prev => ({
                                    ...prev,
                                    id: record.business_id,
                                    display_name: record.display_name,
                                    email: record.contact_email,
                                    type: record.type,
                                }));
                                setIsAddingNew(false);
                                navigate('#contract');
                            } catch (error) {
                                console.error('Error loading billing:', error);
                                message.error('Failed to load contract data.');
                            }
                        }}
                    >
                        {text}
                    </a>
                ) : '-',
        },
        {
            title: 'Business Name',
            dataIndex: 'display_name',
            key: 'display_name',
            render: (text: string, record: any) => text || record.full_name || record.business_id,
            filterDropdown: (props: import('antd/es/table/interface').FilterDropdownProps) => {
                const { setSelectedKeys, selectedKeys, confirm, clearFilters, visible } = props;
                return (
                    <div style={{ padding: 8 }}>
                        <Input
                            autoFocus
                            placeholder="Type to search business name"
                            value={selectedKeys[0] || ''}
                            onChange={e => {
                                setSelectedKeys(e.target.value ? [e.target.value] : []);
                                setSearchInputValue(e.target.value);
                                confirm({ closeDropdown: false });
                            }}
                            style={{ width: 200, marginBottom: 8, display: 'block' }}
                        />
                    </div>
                );
            },
            filterIcon: (filtered: boolean) => (
                <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
            ),
            onFilterDropdownVisibleChange: (visible: boolean) => {
                setSearchDropdownVisible(visible);
                if (!visible) {
                    setSearchInputValue('');
                }
            },
            onFilter: (value: boolean | React.Key, record: any) => {
                if (typeof value === 'string') {
                    return record.display_name && record.display_name.toLowerCase().includes(value.toLowerCase());
                }
                return false;
            },
        },
        {
            title: 'Contact Email',
            dataIndex: 'contact_email',
            key: 'contact_email',
            render: (text: string, record: any) => record.contact_email || '-',
        },
        {
            title: 'Business ID',
            dataIndex: 'business_id',
            key: 'business_id',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            filters: [
                { text: 'Active', value: 'active' },
                { text: 'Inactive', value: 'inactive' },
            ],
            filterMultiple: false,
            filterIcon: (filtered: boolean) => (
                <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
            ),
            onFilter: (value: boolean | React.Key, record: any) => {
                if (!value) return true;
                if (typeof value === 'string') {
                    return record.status === value;
                }
                return false;
            },
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }: {
                setSelectedKeys: (selectedKeys: React.Key[]) => void;
                selectedKeys: React.Key[];
                confirm: () => void;
            }) => (
                <div style={{ padding: 8 }}>
                    <Button
                        type={selectedKeys[0] === 'active' ? 'primary' : 'default'}
                        style={{ width: '100%', marginBottom: 8 }}
                        onClick={() => {
                            setSelectedKeys(['active']);
                            confirm();
                        }}
                    >
                        Active
                    </Button>
                    <Button
                        type={selectedKeys[0] === 'inactive' ? 'primary' : 'default'}
                        style={{ width: '100%' }}
                        onClick={() => {
                            setSelectedKeys(['inactive']);
                            confirm();
                        }}
                    >
                        Inactive
                    </Button>
                </div>
            ),
            render: (text: string) => text || '-',
        },
        {
            title: 'Premium',
            dataIndex: 'premium',
            key: 'premium',
            render: (val: any) => val !== undefined && val !== null ? `${val}%` : '-',
        },
    ];

    // Fetch all billings for the table, sorted by contract_id desc
    useEffect(() => {
        (async () => {
            setAllBillingsLoading(true);
            try {
                await fetchAndSetAllBillings();
            } catch (error) {
                setAllBillings([]);
            } finally {
                setAllBillingsLoading(false);
            }
        })();
    }, []);
    useEffect(() => {
        if (!isAddingNew && data && data.id) {
            (async () => {
                try {
                    const billingResponse = await makeRequest({
                        origin: window.location.origin,
                        method: 'get',
                        url: `/api-billing/${data.id}`,
                        headers: {},
                    });
                    setBilling(billingResponse);
                } catch (error) {
                    setBilling({} as Billing);
                }
            })();
        }
    }, [data.id, isAddingNew]);

    // Fetch last_updated_by username
    useEffect(() => {
        const fetchUsername = async (userId: string) => {
            if (!userId) {
                setLastUpdatedByUsername('');
                return;
            }
            try {
                const response = await makeRequest({
                    origin: window.location.origin,
                    method: 'get',
                    url: `/api-user-username/${userId}`,
                    headers: {},
                });
                setLastUpdatedByUsername(response.username || response.email || userId);
            } catch {
                setLastUpdatedByUsername(userId);
            }
        };
        if (billing && billing.last_updated_by) {
            fetchUsername(billing.last_updated_by);
        } else {
            setLastUpdatedByUsername('');
        }
    }, [billing?.last_updated_by]);

    // Fetch billing users when billing is selected
    useEffect(() => {
        if (billing?.id !== undefined && billing?.id !== null) {
            fetchBillingUsers(billing.id);
        } else {
            setBillingUsers([]);
        }
    }, [billing?.id, fetchBillingUsers]);

    // Fetch contracts for artist users
    useEffect(() => {
        if (data && data.id && data.type === 1) {
            (async () => {
                setContractsLoading(true);
                try {
                    const contractsResponse = await makeRequest({
                        origin: window.location.origin,
                        method: 'get',
                        url: `/api-contracts/${data.id}`,
                        headers: {},
                    });
                    setContracts(Array.isArray(contractsResponse) ? contractsResponse : [])
                } catch (error) {
                    setContracts([]);
                } finally {
                    setContractsLoading(false);
                }
            })();
        } else {
            setContracts([]);
        }
    }, [data.id, data.type]);

    // Fetch all enabled businesses for Add New
    const fetchAddNewBusinessOptions = useCallback(async (search: string = '') => {
        try {
            const response = await makeRequest({
                origin: window.location.origin,
                method: 'get',
                url: `/api-users-search?query=${encodeURIComponent(search)}`,
                headers: {},
            });
            const filtered = response.filter((item: any) => {
                const id = item.id || item.business_id;
                return !existingBusinessIds.includes(id);
            });
            setAddNewBusinessOptions(filtered.map((item: any) => {
                const id = item.id || item.business_id;
                return {
                    label: item.display_name || item.email || item.full_name || '',
                    value: id,
                    data: { ...item, id },
                };
            }));
        } catch {
            setAddNewBusinessOptions([]);
        }
    }, [existingBusinessIds]);
    
    useEffect(() => { 
        if (isAddingNew) {
            fetchAddNewBusinessOptions(); 
        }
    }, [fetchAddNewBusinessOptions, isAddingNew]);

    // Fetch initial dropdown options on mount
    useEffect(() => {
        (async () => {
            setSearchLoading(true);
            try {
                const response = await makeRequest({
                    origin: window.location.origin,
                    method: 'get',
                    url: '/api-billing-search?query=',
                    headers: {},
                });
                setSearchOptions(response.map((item: any) => {
                    const id = item.id || item.business_id;
                    return {
                        label: item.full_name || item.display_name || '',
                        value: id,
                        data: { ...item, id, type: item.type },
                    };
                }));
                setExistingBusinessIds(response.map((item: any) => item.business_id || item.id));
            } catch (error) {
                setSearchOptions([]);
                setExistingBusinessIds([]);
            }
            setSearchLoading(false);
        })();
    }, []);

    // Handler for Add NEW button: open a blank contract form directly
    const handleAddNew = () => {
        setIsAddingNew(true);
        setData({} as User);
        setBilling({} as Billing);
        setContracts([]);
        setAddNewBusinessId(undefined);
        setSearchValue('');
        setAddNewErrors({});
    };

    // Handler for canceling Add New mode
    const handleCancelAddNew = () => {
        setIsAddingNew(false);
        setAddNewBusinessId(undefined);
        setData({} as User);
        setBilling({} as Billing);
        setContracts([]);
        setAddNewErrors({});
    };

    // Handler for searching in the Select dropdown
    const handleSearch = async (value: string) => {
        setSearchLoading(true);
        try {
            const response = await makeRequest({
                origin: window.location.origin,
                method: 'get',
                url: `/api-billing-search?query=${encodeURIComponent(value)}`,
                headers: {},
            });
            setSearchOptions(response.map((item: any) => {
                const id = item.id || item.business_id;
                return {
                    label: item.full_name || item.contact_email || item.username || item.display_name || '',
                    value: id,
                    data: { ...item, id, type: item.type },
                };
            }));
        } catch (error) {
            setSearchOptions([]);
        }
        setSearchLoading(false);
    };

    // Handler for selecting an option in the Select dropdown
    const handleSelect = (value: string, option: any) => {
        setSearchValue(value);
        setData(option.data);
    };

    // Date change handlers
    const onChangeStartDate: DatePickerProps['onChange'] = (date) => {
        const newStartDate = date && !Array.isArray(date) ? date.toDate() : null;
        setBilling({ ...billing, start_date: newStartDate });
        if (addNewErrors.start_date) {
            setAddNewErrors(prev => {
                const { start_date, ...rest } = prev;
                return rest;
            });
        }
        if (billing.end_date && newStartDate) {
            if (dayjs(billing.end_date).isBefore(dayjs(newStartDate)) || dayjs(billing.end_date).isSame(dayjs(newStartDate))) {
                setAddNewErrors(prev => ({ ...prev, end_date: 'End date must be after start date' }));
            } else {
                setAddNewErrors(prev => {
                    const { end_date, ...rest } = prev;
                    return rest;
                });
            }
        }
    };

    const onChangeEndDate: DatePickerProps['onChange'] = (date) => {
        const newEndDate = date && !Array.isArray(date) ? date.toDate() : null;
        setBilling({ ...billing, end_date: newEndDate });
        setAddNewErrors(prev => {
            const { end_date, ...rest } = prev;
            return rest;
        });
        if (billing.start_date && newEndDate) {
            if (dayjs(newEndDate).isBefore(dayjs(billing.start_date)) || dayjs(newEndDate).isSame(dayjs(billing.start_date))) {
                setAddNewErrors(prev => ({ ...prev, end_date: 'End date must be after start date' }));
            }
        }
    };

    // Save billing changes
    const saveBilling = async () => {
        // Validation for Add New
        if (isAddingNew) {
            const errors: { [key: string]: string } = {};
            if (billing.premium === undefined || billing.premium === null || (typeof billing.premium === 'string' && billing.premium === '')) {
                errors.premium = 'Business premium is required';
            }
            if (!billing.start_date) {
                errors.start_date = 'Start date is required';
            }
            if (!billing.end_date) {
                errors.end_date = 'End date is required';
            }
            if (billing.start_date && billing.end_date && (dayjs(billing.end_date).isBefore(dayjs(billing.start_date)) || dayjs(billing.end_date).isSame(dayjs(billing.start_date)))) {
                errors.end_date = 'End date must be after start date';
            }
            if (!billing.full_name || billing.full_name.trim() === '') {
                errors.full_name = 'Full name is required';
            }
            if (!billing.contact_email || billing.contact_email.trim() === '') {
                errors.contact_email = 'Contact email is required';
            }
            setAddNewErrors(errors);
            if (Object.keys(errors).length > 0) {
                message.error('Please fill all required fields correctly.');
                return;
            }
        }

        setLoading(true);
        try {
            const billingToSave = { ...billing, business_id: data.id };
            let response;
            if (billing && billing.id) {
                response = await makeRequest({
                    origin: window.location.origin,
                    method: 'put',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    url: '/api-update-billing',
                    data: { billing: billingToSave },
                });
                message.success('Billing data updated successfully!');
            } else {
                response = await makeRequest({
                    origin: window.location.origin,
                    method: 'post',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    url: '/api-create-billing',
                    data: { billing: billingToSave },
                });
                message.success('Billing data created successfully!');
            }
            setBilling(response);
            setAddNewErrors({});
            if (isAddingNew) {
                setIsAddingNew(false);
                setAddNewBusinessId(undefined);
                setBilling({} as Billing);
                setData({} as User);
                await fetchAndSetAllBillings();
                setHighlightedRowKey(response.id || response.business_id);
                setTimeout(() => setHighlightedRowKey(undefined), 3000);
            } else {
                await fetchAndSetAllBillings();
            }
        } catch (error) {
            message.error('Failed to save billing data.');
        } finally {
            setLoading(false);
        }
    };

    // Add empty contract handler
    const addEmptyContract = () => {
        const emptyContract: ArtistRep = {
            id: '',
            artist_id: data.id || '',
            dealer_profile: '',
            dealer_username: '',
            rep_id: '',
            rep_type: 0,
            status: 'draft',
            fee: 0,
            creation_date: null,
            last_updated_Date: null,
            last_updated_by: '',
            editing: true,
        };
        setContracts([...contracts, emptyContract]);
        setHasNewContract(true);
    };

    // Add New form rendering logic
    const renderAddNewForm = () => (
        <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ flex: 1 }}>
                    <label>Select Business</label>
                    <Select
                        showSearch
                        value={addNewBusinessId}
                        placeholder="Search business by name or email"
                        filterOption={false}
                        onSearch={fetchAddNewBusinessOptions}
                        dropdownMatchSelectWidth={false}
                        onSelect={(value, option) => {
                            setAddNewBusinessId(value);
                            setData(option.data);
                            setTimeout(() => {
                                const dropdown = document.activeElement as HTMLElement;
                                if (dropdown && dropdown.blur) dropdown.blur();
                            }, 0);
                        }}
                        optionLabelProp="label"
                        options={addNewBusinessOptions.map(opt => ({
                            ...opt,
                            label: opt.label,
                            children: (
                                <Tooltip title={opt.data && opt.data.contact_email} placement="right">
                                    <div style={{ display: 'flex', flexDirection: 'column', width: 300, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis' }}>{opt.label}</div>
                                        {opt.data && opt.data.contact_email && (
                                            <div style={{ fontSize: 12, color: '#888', overflow: 'hidden', textOverflow: 'ellipsis' }}>{opt.data.contact_email}</div>
                                        )}
                                    </div>
                                </Tooltip>
                            )
                        }))}
                        style={{ minWidth: 320, width: 320 }}
                    />
                </div>
                <Button onClick={handleCancelAddNew}>Cancel</Button>
            </div>
        </div>
    );

    const contractsPercent = Array.isArray(contracts) ? contracts.reduce((sum, contract) => sum + contract.fee, 0) : 0;
    return (
        <>
            {contextHolder}


            {/* All contracts table - only show if no contract is selected or adding new */}
            {(billing?.id === undefined || billing?.id === null) && !isAddingNew && (
                <div style={{ marginBottom: 32 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Title level={4} style={{ margin: 0 }}>All Billing Contracts</Title>
                        <Button type="primary" onClick={handleAddNew} style={{ minWidth: 140 }}>Create New</Button>
                    </div>
                    <br />
                    <Table
                        dataSource={allBillings.filter(row =>
                            !searchInputValue || (row.display_name && row.display_name.toLowerCase().includes(searchInputValue.toLowerCase()))
                        )}
                        columns={billingTableColumns}
                        rowKey={record => record.id ?? record.business_id}
                        loading={allBillingsLoading}
                        pagination={false}
                        size="small"
                        rowClassName={record => (highlightedRowKey && (record.id === highlightedRowKey || record.business_id === highlightedRowKey) ? 'highlighted-row' : '')}
                        onRow={record => ({
                            onClick: async () => {
                                console.log('[Contracts UI] Clicked row:', record);
                                try {
                                    const billingResponse = await makeRequest({
                                        origin: window.location.origin,
                                        method: 'get',
                                        url: `/api-billing/${record.id ?? record.business_id}`,
                                        headers: {},
                                    });
                                    console.log('[Contracts UI] API Billing Response:', billingResponse);
                                    setBilling(billingResponse);
                                    
                                    const newData = {
                                        id: record.business_id,
                                        display_name: record.display_name,
                                        email: record.contact_email,
                                        type: record.type,
                                    };
                                    console.log('[Contracts UI] Setting new data:', newData);
                                    
                                    setData(prev => ({
                                        ...prev,
                                        ...newData
                                    }));
                                    setIsAddingNew(false);
                                    navigate('#contract');
                                } catch (error) {
                                    console.error('[Contracts UI] Error loading contract:', error);
                                    message.error('Failed to load contract data.');
                                }
                            }
                        })}
                    />
                            {/* Highlighted row style */}
                            <style>{`
                                .highlighted-row {
                                    background: #ffe58f !important;
                                    transition: background 0.5s;
                                }
                            `}</style>
                </div>
            )}

            {isAddingNew && renderAddNewForm()}

            {console.log('[Contracts UI Render] isAddingNew:', isAddingNew, 'billing:', billing, 'data:', data)}
            {((isAddingNew && addNewBusinessId && data && (data.type === 3 || data.type === 4)) || (!isAddingNew && billing && billing.id !== undefined && billing.id !== null && data && (data.type === 3 || data.type === 4 || String(billing.contract_id) === '0'))) && (
                <div id="contract">
                    <Button
                        style={{ marginBottom: 16 }}
                        onClick={async () => {
                            setIsAddingNew(false);
                            setBilling({} as Billing);
                            setData({} as User);
                            setAddNewBusinessId(undefined);
                            await fetchAndSetAllBillings();
                        }}
                    >
                        Back
                    </Button>
                    <>
                        <Title className='mt-0' level={4}>Billing data</Title>
                        {(billing?.id !== undefined && billing?.id !== null) && (
                            <>
                                <Text>Contract ID</Text>
                                <Input
                                    className="mb-5"
                                    value={billing?.id !== undefined && billing?.id !== null ? String(billing.id) : ''}
                                    readOnly
                                    disabled
                                />
                            </>
                        )}
                        <Text>Business ID</Text>
                        <Input
                            className="mb-5"
                            value={data?.id || ''}
                            readOnly
                            disabled
                        />
                        <Row gutter={16} className="mb-5" align="middle">
                            <Col span={24} style={{display:'flex',alignItems:'center',gap:24,flexWrap:'wrap'}}>
                                <div style={{display:'flex',alignItems:'center',gap:8}}>
                                    <Text>business premium <span style={{color:'red'}}>*</span></Text>
                                    <Input
                                        id="business-premium-input"
                                        type="number"
                                        min={0}
                                        max={100}
                                        value={billing?.premium ?? ''}
                                        onChange={e => {
                                            const val = e.target.value;
                                            setBilling({ ...billing, premium: val === '' ? undefined : Math.max(0, Math.min(100, Number(val))) });
                                            if (addNewErrors.premium) {
                                                setAddNewErrors(prev => {
                                                    const { premium, ...rest } = prev;
                                                    return rest;
                                                });
                                            }
                                        }}
                                        suffix="%"
                                        status={addNewErrors.premium ? 'error' : ''}
                                        style={{ width: 100 }}
                                    />
                                    {addNewErrors.premium && <div style={{color:'red',marginTop:4,fontSize:12}}>{addNewErrors.premium}</div>}
                                </div>
                                <div style={{display:'flex',alignItems:'center',gap:16}}>
                                    <div style={{display:'flex',alignItems:'center',gap:8}}>
                                        <Text>Status</Text>
                                        <Switch
                                            checked={billing?.status === 'active'}
                                            onChange={checked => setBilling({ ...billing, status: checked ? 'active' : 'inactive' })}
                                        />
                                    </div>
                                    <div style={{display:'flex',alignItems:'center',gap:8}}>
                                        <Text>Escrow</Text>
                                        <Switch
                                            checked={billing?.is_escrow_enabled}
                                            onChange={checked => setBilling({ ...billing, is_escrow_enabled: checked })}
                                        />
                                    </div>
                                    <div style={{display:'flex',alignItems:'center',gap:8}}>
                                        <Text>BizAdmin</Text>
                                        <Switch
                                            checked={billing?.is_bizadmin_enabled}
                                            onChange={checked => setBilling({ ...billing, is_bizadmin_enabled: checked })}
                                        />
                                    </div>
                                    <div style={{display:'flex',alignItems:'center',gap:8}}>
                                        <Text>POS</Text>
                                        <Switch
                                            checked={billing?.is_pos_enabled}
                                            onChange={checked => setBilling({ ...billing, is_pos_enabled: checked })}
                                        />
                                    </div>
                                </div>
                            </Col>
                        </Row>
                        <Row gutter={8}>
                            <Col span={24} md={12}>
                                <Text>Start date <span style={{color:'red'}}>*</span></Text>
                                <DatePicker 
                                    value={billing?.start_date ? dayjs(billing?.start_date) : null} 
                                    showTime 
                                    className="mb-5" 
                                    style={{ display: "block" }} 
                                    onChange={onChangeStartDate}
                                    status={addNewErrors.start_date ? 'error' : ''} 
                                />
                                {addNewErrors.start_date && <div style={{color:'red',marginTop:-12,marginBottom:8,fontSize:12}}>{addNewErrors.start_date}</div>}
                            </Col>
                            <Col span={24} md={12}>
                                <Text>End date <span style={{color:'red'}}>*</span></Text>
                                <DatePicker 
                                    value={billing?.end_date ? dayjs(billing?.end_date) : null} 
                                    showTime 
                                    className="mb-5" 
                                    style={{ display: "block" }} 
                                    onChange={onChangeEndDate}
                                    status={addNewErrors.end_date ? 'error' : ''} 
                                />
                                {addNewErrors.end_date && <div style={{color:'red',marginTop:-12,marginBottom:8,fontSize:12}}>{addNewErrors.end_date}</div>}
                            </Col>
                        </Row>
                        <Text>Full name <span style={{color:'red'}}>*</span></Text>
                        <Input
                            className="mb-5"
                            value={billing?.full_name ?? ''}
                            onChange={(e) => {
                                setBilling({ ...billing, full_name: e.target.value === '' ? "" : e.target.value });
                                if (addNewErrors.full_name) {
                                    setAddNewErrors(prev => {
                                        const { full_name, ...rest } = prev;
                                        return rest;
                                    });
                                }
                            }}
                            status={addNewErrors.full_name ? 'error' : ''}
                        />
                        {addNewErrors.full_name && <div style={{color:'red',marginTop:-12,marginBottom:8,fontSize:12}}>{addNewErrors.full_name}</div>}
                        <Text>Business Contact Email <span style={{color:'red'}}>*</span></Text>
                        <Input
                            className="mb-5"
                            value={billing?.contact_email ?? ''}
                            onChange={(e) => {
                                setBilling({ ...billing, contact_email: e.target.value === '' ? "" : e.target.value });
                                if (addNewErrors.contact_email) {
                                    setAddNewErrors(prev => {
                                        const { contact_email, ...rest } = prev;
                                        return rest;
                                    });
                                }
                            }}
                            status={addNewErrors.contact_email ? 'error' : ''}
                        />
                        {addNewErrors.contact_email && <div style={{color:'red',marginTop:-12,marginBottom:8,fontSize:12}}>{addNewErrors.contact_email}</div>}
                        <Text>GoogleId (Place Holder)</Text>
                        <Input
                            className="mb-5"
                            value={billing?.google_id ?? ''}
                            onChange={(e) => setBilling({ ...billing, google_id: e.target.value === '' ? "" : e.target.value })}
                        />
                        <Row gutter={8}>
                            <Col span={24} md={12}>
                                <Text>escrow id (Place Holder)</Text>
                                <Input disabled className='mb-5' value={billing?.escrow_id || ''} />
                            </Col>
                            <Col span={24} md={12}>
                                <Text>escrow premium (Place Holder)</Text>
                                <Input className='mb-5' value={billing?.escrow_premium || ''}
                                    onChange={(e) => setBilling({ ...billing, escrow_premium: e.target.value === '' ? null : parseInt(e.target.value) || 0 })} />
                            </Col>
                        </Row>
                        <Button
                            className='mt-4'
                            type="primary"
                            icon={<SaveOutlined />}
                            loading={loading}
                            onClick={saveBilling}
                        >
                            Save
                        </Button>
                        {(billing?.last_updated_by || billing?.last_updated_date) && (
                            <div style={{ marginTop: 24, textAlign: 'right', color: '#888', fontSize: 13 }}>
                                {billing?.last_updated_by && (
                                    <span>Last updated by: <b>{lastUpdatedByUsername}</b></span>
                                )}
                                {billing?.last_updated_date && (
                                    <span style={{ marginLeft: 16 }}>
                                        on {billing.last_updated_date ? dayjs(billing.last_updated_date).format('YYYY-MM-DD HH:mm') : ''}
                                    </span>
                                )}
                            </div>
                        )}

                        {/* Authorized Users Section */}
                        {(billing?.id !== undefined && billing?.id !== null) && (
                            <>
                                <Divider style={{ marginTop: 32, marginBottom: 24 }} />
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                                    <Title level={5} style={{ margin: 0 }}>Authorized Users ({billingUsers.length})</Title>
                                    <Button
                                        type="primary"
                                        icon={<PlusOutlined />}
                                        onClick={() => setAddBillingUserModalOpen(true)}
                                        size="small"
                                    >
                                        Add User
                                    </Button>
                                </div>
                                <Table
                                    dataSource={billingUsers}
                                    rowKey="id"
                                    loading={billingUsersLoading}
                                    pagination={false}
                                    size="small"
                                    columns={[
                                        {
                                            title: 'Email',
                                            dataIndex: 'email',
                                            key: 'email',
                                            render: (email: string, record: any) => (
                                                <div>
                                                    <div>{email}</div>
                                                    {record.display_name && (
                                                        <div style={{ fontSize: 12, color: '#888' }}>
                                                            {record.display_name}
                                                        </div>
                                                    )}
                                                </div>
                                            ),
                                        },
                                        {
                                            title: 'Google ID',
                                            dataIndex: 'google_id',
                                            key: 'google_id',
                                            width: 100,
                                            render: (googleId: string) => (
                                                <span style={{
                                                    color: googleId ? '#52c41a' : '#faad14',
                                                    fontWeight: 500
                                                }}>
                                                    {googleId ? 'Ready' : 'Pending'}
                                                </span>
                                            ),
                                        },
                                        {
                                            title: 'Created Date',
                                            dataIndex: 'creation_date',
                                            key: 'creation_date',
                                            width: 180,
                                            render: (date: string) =>
                                                date ? dayjs(date).format('YYYY-MM-DD HH:mm') : '-',
                                        },
                                        {
                                            title: 'Status',
                                            dataIndex: 'is_active',
                                            key: 'is_active',
                                            width: 120,
                                            render: (isActive: boolean, record: any) => (
                                                <Switch
                                                    checked={isActive}
                                                    onChange={() => handleToggleUserStatus(record.id, isActive)}
                                                    checkedChildren="Active"
                                                    unCheckedChildren="Inactive"
                                                />
                                            ),
                                        },
                                        {
                                            title: 'Actions',
                                            key: 'actions',
                                            width: 100,
                                            render: (_: any, record: any) => (
                                                <Button
                                                    type="text"
                                                    danger
                                                    icon={<DeleteOutlined />}
                                                    onClick={() => handleRemoveUser(record.id, record.email)}
                                                    size="small"
                                                >
                                                    Remove
                                                </Button>
                                            ),
                                        },
                                    ]}
                                />
                                <AddBillingUserModal
                                    open={addBillingUserModalOpen}
                                    billingId={billing.id}
                                    onClose={() => setAddBillingUserModalOpen(false)}
                                    onSuccess={() => fetchBillingUsers(billing.id)}
                                />
                            </>
                        )}
                    </>
                </div>
            )}

            {data && data.id && data.type === 1 && (billing?.id === undefined || billing?.id === null || String(billing?.contract_id) === '0') && !isAddingNew && (
                <>
                    <Row>
                        <Col span={12}>
                            <Title className='mt-0' level={4}>Contracts</Title>
                            <Progress percent={contractsPercent} steps={10}
                                status="active"
                                strokeColor={[green[2], green[3], green[4], orange[3], orange[4], orange[5], red[4], red[5], red[6], red[7]]}
                            />
                            <p className='mt-0'>
                                <Text className='mt-0' type="secondary">artist's committed percentage</Text>
                            </p>
                        </Col>
                        <Col span={12}>
                        </Col>
                    </Row>
                    {contractsLoading ? (
                        <div>Loading contracts...</div>
                    ) : (!Array.isArray(contracts) || contracts.length === 0) ? (
                        <Empty
                            imageStyle={{ height: 60 }}
                            description={
                                <Typography.Text>
                                    no contract found
                                </Typography.Text>
                            }
                        >
                            <Button onClick={addEmptyContract}>Create Now</Button>
                        </Empty>
                    ) : (
                        <div key={contractsKey}>
                            {!hasNewContract && Array.isArray(contracts) && contracts.length <= 4 &&
                                <Button
                                    className='mt-4'
                                    type="primary" onClick={addEmptyContract}>Create contract</Button>
                            }
                            {Array.isArray(contracts) && contracts.map((contract, i) => (
                                <RepresentationContract
                                    key={contract.id || i}
                                    contract={contract}
                                    contracts={contracts}
                                    setContracts={setContracts}
                                    index={i}
                                    hasNewContract={hasNewContract}
                                    setHasNewContract={setHasNewContract}
                                    loading={false}
                                    totalUsedFee={contractsPercent} />
                            ))}
                        </div>
                    )}
                </>
            )}
        </>
    );
}

export default ContractsScreen;